import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { formatDate } from '@/lib/format'
import type { AdminFetch } from './AdminCustomers'

/** What `/admin/stamps` answers: the dealer's own stamps at SW and the company accounts still being fed. */
export type StampOverview = {
  configured: boolean
  dealer?: { available: number; used: number } | null
  threshold: number
  pending: Array<{ companyId: string; businessName?: string | null; attempts: number; lastError?: string | null; createdAt: string }>
}

/**
 * A slim band over the prices: the stamps left in the dealer's account at SW, from which every company's account is
 * fed, in the danger colour under the threshold; and the deliveries to SW still pending, each with its last error.
 */
export function AdminStamps({ adminFetch, describeError }: { adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string }) {
  const [overview, setOverview] = useState<StampOverview | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try { setOverview(await adminFetch<StampOverview>('/admin/stamps')) }
    catch (cause) { setError(describeError(cause, 'No pudimos leer la bolsa de timbres.')) }
    finally { setLoading(false) }
  }, [adminFetch, describeError])

  useEffect(() => { void load() }, [load])

  const dealer = overview?.dealer
  const low = Boolean(dealer && overview && dealer.available < overview.threshold)
  const count = (value: number) => value.toLocaleString('es-MX')
  let summary: string
  if (loading && !overview) summary = 'leyendo…'
  else if (error) summary = error
  else if (!overview?.configured) summary = 'SW no está configurado'
  else if (!dealer) summary = 'SW no respondió'
  else summary = `${count(dealer.available)} disponibles · ${count(dealer.used)} usados`

  return <section className="admin-stamps" aria-label="Bolsa de timbres en SW" data-tone={low || error ? 'low' : undefined}>
    <div className="admin-stamps-line">
      <p><span>Bolsa de timbres en SW:</span> <strong>{summary}</strong>{low && overview && <> · por debajo de {count(overview.threshold)}</>}</p>
      {(error || (overview?.configured && !dealer)) && <Button emphasis="ghost" intent="neutral" busy={loading} onClick={() => void load()}>Reintentar</Button>}
    </div>
    {overview && overview.pending.length > 0 && <ul className="admin-stamps-pending" aria-label="Entregas pendientes a SW">
      {overview.pending.map((item) => <li key={`${item.companyId}-${item.createdAt}`}>
        <strong>{item.businessName ?? item.companyId}</strong>
        <span>Desde el {formatDate(item.createdAt)} · {item.attempts} {item.attempts === 1 ? 'intento' : 'intentos'}{item.lastError ? ` · ${item.lastError}` : ''}</span>
      </li>)}
    </ul>}
  </section>
}
